import { useUrlSearchParams } from './useUrlSearchParams';

interface PageState<TTab> {
    page: number;
    pageSize: number;
    sortDirection: 'asc' | 'desc';
    sortField: string;
    search: string;
    tab: TTab;
    filters: Record<string, any>;
}

interface SortFieldParams {
    field: string;
    direction: 'asc' | 'desc';
}

interface UseDataTableStateOptions<TTab> {
    defaultPageSize?: number;
    defaultSortDirection?: 'asc' | 'desc';
    defaultSortField?: string;
    defaultFilterValues?: Record<string, any>;
    defaultTab?: TTab;
}

export function useDataTableState<TFilters = void | Record<string, any>, TTab = string>(options: UseDataTableStateOptions<TTab>) {
    const { urlSearchParams, setUrlSearchParams } = useUrlSearchParams<PageState<TTab>>( {
        page: 0,
        pageSize: options?.defaultPageSize ?? 10,
        sortField: options?.defaultSortField || '',
        sortDirection: options?.defaultSortDirection ?? 'asc',
        search: '',
        filters: options?.defaultFilterValues ?? {},
        tab: (options?.defaultTab ?? '') as TTab
    });

    return {
        page: urlSearchParams.page,
        pageSize: urlSearchParams.pageSize,
        sortParams: { field: urlSearchParams?.sortField, direction: urlSearchParams?.sortDirection },
        search: urlSearchParams?.search,
        filters: urlSearchParams?.filters as TFilters,
        tab: urlSearchParams.tab as TTab,
        setPage: (page: number) => {
            setUrlSearchParams({ page });
        },
        setPageSize: (pageSize: number) => {
            setUrlSearchParams({
                pageSize,
                page: 0
            });
        },
        setSort: (sortParams: SortFieldParams) => {
            setUrlSearchParams({
                sortField: sortParams.field,
                sortDirection: sortParams.direction,
                page: 0
            });
        },
        setSearch: (search: string) => {
            setUrlSearchParams({
                search,
                page: 0
            });
        },
        setFilters: (filterValues: Record<string, any>) => {
            setUrlSearchParams({
                page: 0,
                filters: filterValues
            });
        },
        setTab: (value: string, otherParams = {}) => {
            setUrlSearchParams({
                tab: value,
                ...otherParams
            });
        }
    };
}
